import { useInfiniteQuery, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { isEmpty, isNil } from 'lodash'
import { queryClient } from '~/common/react-query/queryClient'
import api from '~/settings/business-unit/api'
import { Log } from '~/settings/settingsApi'
import { TreeSelectPathSegment } from '~/design_system'
import { OffsetLimitPaginationResponse } from './common'

interface BusinessUnitAncestor {
  id: number
  name: string
  level: number
  sub_label?: string | null
}

interface WorkdayBusinessUnitConnectorSettings {
  is_position_management_model: boolean
}

interface BusinessUnitConnectorSettings {
  workday?: WorkdayBusinessUnitConnectorSettings
}

type BusinessUnitPathSegment = TreeSelectPathSegment

export interface BusinessUnit {
  id: number
  name: string
  code: string
  created_by: number
  updated_by: number
  has_access: boolean
  has_descendant_access: boolean
  time_keeping: boolean
  is_deleted: boolean
  level: number | null
  level_name?: string | null
  description?: string | null
  resource_managers?: number[] | null
  resource_manager_names?: string[] | []
  parent: number | null
  parent_name?: string | null
  children: number[]
  ancestors?: BusinessUnitAncestor[]
  generic_fields?: Record<string, any>
  connector_settings: BusinessUnitConnectorSettings
  path?: string
  path_segments?: BusinessUnitPathSegment[]
  sub_label?: string
}

export interface BusinessUnitLevel {
  id: number
  name: string
  is_system: boolean
  created_by: number
  updated_by: number
  created_at: string
  updated_at: string
  level: number
  sub_label?: string
}

export interface AddLevelRequestData {
  name: string
}

export type BusinessUnitsResponse = BusinessUnit[]
export type BusinessUnitLevelResponse = BusinessUnitLevel[]

export type BusinessUnitConfig = {
  show_sub_label_on_view: boolean
  show_sub_label_on_edit: boolean
  sub_label_key: string
  generic_fields: string[]
  standardize_sub_labels?: boolean
  is_org_hierarchy_editable: boolean
  can_manage_settings: boolean
  company_name: string
}

const BUSINESS_UNITS_QUERY = 'business_units'
const BUSINESS_LEVEL_KEY = 'business_unit_level'
const CONFIG_KEY = 'config'

export const useBusinessUnitsQuery = (params: any) => {
  const KEY = [BUSINESS_UNITS_QUERY, 'parent', params?.parent || params]
  return useQuery({
    queryKey: KEY,
    queryFn: (): Promise<BusinessUnit[]> =>
      api.businessUnit.list({
        ...params,
        isDeleted: null,
        paginated: false
      }),
    enabled: !isEmpty(params),
    staleTime: Infinity
  })
}

export const useBusinessUnitsQueryForPicker = (params: any) => {
  const KEY = [BUSINESS_UNITS_QUERY, 'for-picker', params]
  return useQuery({
    queryKey: KEY,
    queryFn: (): Promise<OffsetLimitPaginationResponse<BusinessUnit>> =>
      api.businessUnit.list_paginated({
        ...params,
        isDeleted: false
      }),
    enabled: !isEmpty(params)
  })
}

export const useClientBusinessUnitQuery = () =>
  useQuery({
    queryKey: [BUSINESS_UNITS_QUERY, 'clientLevel'],
    queryFn: async () => {
      // @ts-ignore
      const l1 = await api.businessUnit.list({ minLevel: 1, paginated: false })
      return l1[0]
    }
  })

export const BUSINESS_LEVEL_ACTIVITY_LOG_KEY = `${BUSINESS_LEVEL_KEY}/activity_logs`
export const BUSINESS_UNIT_ACTIVITY_LOG_KEY = `${BUSINESS_UNITS_QUERY}/activity_logs`

export const useBusinessLevelQuery = () =>
  useQuery({
    queryKey: [BUSINESS_LEVEL_KEY],
    queryFn: (): Promise<BusinessUnitLevelResponse> => api.businessUnitLevels.list({ responseCache: { ttl: 0 } })
  })

export const useBusinessUnitDetailQuery = (id?: number, opts?: UseQueryOptions<BusinessUnit>) =>
  useQuery({
    queryKey: [BUSINESS_UNITS_QUERY, 'details', id],
    queryFn: () => (id ? api.businessUnit.get(id!, { responseCache: { ttl: 0 } }) : false),
    ...opts
  })

export const useBusinessUnitConfigQuery = () => {
  const KEY = [BUSINESS_UNITS_QUERY, CONFIG_KEY] as const
  const { isLoading, data } = useQuery({ queryKey: KEY, queryFn: () => api.businessUnit.config() })
  return { isLoading, data }
}

export const useAddBusinessLevelMutation = (
  opts?: UseMutationOptions<BusinessUnitLevelResponse, unknown, AddLevelRequestData>
) =>
  useMutation({
    mutationFn: data => api.businessUnitLevels.addLevel(data),
    ...opts,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries([BUSINESS_LEVEL_KEY])
      await queryClient.invalidateQueries([BUSINESS_LEVEL_ACTIVITY_LOG_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useRenameBusinessLevelMutation = (
  opts?: UseMutationOptions<BusinessUnitLevelResponse, unknown, { id: number } & AddLevelRequestData>
) =>
  useMutation({
    mutationFn: data => {
      const { id, ...rest } = data
      return api.businessUnitLevels.renameLevel(id, rest)
    },
    ...opts,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries([BUSINESS_LEVEL_KEY])
      await queryClient.invalidateQueries([BUSINESS_LEVEL_ACTIVITY_LOG_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

const ACTIVITY_LOG_PAGE_SIZE = 30

export const useBusinessUnitActivityLogsQuery = () =>
  useInfiniteQuery({
    queryKey: [BUSINESS_UNIT_ACTIVITY_LOG_KEY],
    queryFn: ({ pageParam = 0 }): Promise<OffsetLimitPaginationResponse<Log>> =>
      api.businessUnit.getActivityLogs({ limit: ACTIVITY_LOG_PAGE_SIZE, offset: Number(pageParam) }),
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })

export const useBusinessUnitLevelActivityLogsQuery = () =>
  useInfiniteQuery({
    queryKey: [BUSINESS_LEVEL_ACTIVITY_LOG_KEY],
    queryFn: ({ pageParam = 0 }): Promise<OffsetLimitPaginationResponse<Log>> =>
      api.businessUnitLevels.getActivityLogs({ limit: ACTIVITY_LOG_PAGE_SIZE, offset: Number(pageParam) }),
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })

export const invalidateBusinessUnitQueryByParentId = async (parentId: number) => {
  await queryClient.invalidateQueries([BUSINESS_UNITS_QUERY, 'parent', parentId])
}

export const useAddBusinessUnitMutation = (opts?: UseMutationOptions<BusinessUnit, unknown, Partial<BusinessUnit>>) =>
  useMutation({
    mutationFn: data => api.businessUnit.add(data),
    ...opts,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries([BUSINESS_UNIT_ACTIVITY_LOG_KEY])
      invalidateBusinessUnitQueryByParentId(args[1]?.parent as number)
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useEditBusinessUnitMutation = (opts?: UseMutationOptions<BusinessUnit, unknown, Partial<BusinessUnit>>) =>
  useMutation({
    mutationFn: ({ id, ...data }) => api.businessUnit.edit(id, data),
    ...opts,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries([BUSINESS_UNIT_ACTIVITY_LOG_KEY])
      await queryClient.invalidateQueries([BUSINESS_UNITS_QUERY, 'details', args[1]?.id])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useToggleActivationBusinessUnitMutation = (
  opts?: UseMutationOptions<BusinessUnit, unknown, Partial<BusinessUnit>>
) =>
  useMutation({
    mutationFn: ({ id, ...data }) => api.businessUnit.toggle(id, { id, ...data }),
    ...opts,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries([BUSINESS_UNIT_ACTIVITY_LOG_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useIsWorkdayPositionManagementModelQuery = (buId: number | undefined) =>
  useQuery({
    queryKey: ['workday', 'management-model', buId],
    queryFn: () => api.businessUnit.isWorkdayPositionManagementModel(buId),
    enabled: !isNil(buId)
  })
